import {
    Button, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, IconButton,
    Typography
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import ClearIcon from '@material-ui/icons/Clear';
import { format, parseISO } from 'date-fns';
import ContextMenu from 'devextreme-react/context-menu';
import { Column, ColumnChooser, DataGrid, Selection, Sorting } from 'devextreme-react/data-grid';
import Query from 'devextreme/data/query';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { appNotify, closeViewOpportunityHistoryDetails, fetchOpportunityHistoryTableDetails } from 'store/actions';
import { ViewOpportunityHistoryGridConfig as gridConfig } from './ViewOpportunityHistoryDetailsGridConfig';
import './ViewOpportunityHistoryDetailsModal.css';

const ViewOpportunityHistoryDetailsModal = ({
    appNotify,
    classes,
    txnOppId,
    eventSummary,
    closeViewOpportunityHistoryDetails,
    isViewOpportunityHistoryDetailsModalLoading,
    isViewOpportunityHistoryDetailsModalOpen,
    opportunityEditHistories,
    opportunityStatusHistories,
    opportunityTaxesHistories,
    opportunityCrmSubmissionHistories,
    opportunityAllocationHistories,
    opportunityCrmReceiptHistories,
    opportunityValuationHistories,
    opportunityIcbmOrderHistories,
    opportunityImportCompHistories,
    opportunityImportSaveHistories,
    opportunityImportPropMasterHistories,
    opportunityImportHomesHistories,
    opportunityImportRegionsAndIndicativesHistories,
    opportunityImportMarketHistories,
    opportunityImportRepairEstimateHistories,
    opportunityImportExpectedSalesPriceHistories,
    opportunityImportInsuranceHistories,
    opportunityTransactionOpportunityHistories,
    opportunityProformaInputsContainers,
    opportunity,
    opportunityProspects,
    opportunityRejectedImports,
    opportunityAllocationLogs,
    opportunityImportLogs,
    opportunityAssignmentHistories,
    opportunityUnderwriteStatusHistories,
    appSettings
}) => {

    const gridRef = useRef(null);
    const secondGridRef = useRef(null);
    const thirdGridRef = useRef(null);
    const contextMenuRef = useRef(null);

    const [viewChangesOnly, setViewChangesOnly] = useState(false);

    const handleDetailsClose = () => {
        setViewChangesOnly(false);
        closeViewOpportunityHistoryDetails();
    }

    const handleIcbmOrdersOnRowPrepared = (row) => {
        if (row.rowType === 'data' && row.data.fetchedDate === eventSummary.tableDateKey) {
            row.rowElement.classList.add('dx-row-focused');
        }
    }

    const handleMarketsOnRowPrepared = (row) => {
        if (row.rowType === 'data' && row.data.createdOn === eventSummary.eventDate) {
            row.rowElement.classList.add('dx-row-focused');
        }
    }

    const handleHighlightOnRowPrepared = (row) => {
        if(row.rowType === 'data' && viewChangesOnly) {
            row.rowElement.style.height = '36px'
        }
        if (row.rowType === 'data' && row.data.id === eventSummary.tableId) {
            row.rowElement.classList.add('dx-row-focused');
        }
    }

    const getColumns = useCallback((tableName) => {
        const matchingRecords = appSettings.TxnOppHistorySettings.DetailsGridFieldSettings.filter(record => {
            return record.table === tableName;
        });

        return matchingRecords;

    }, [appSettings.FieldSettings]);

    const createDataTableColumnsCrmHist = (tableName, firstRow, changes) => {
        // Return if there is no data
        if (!firstRow) {
            return;
        }

        const columnSettings = getColumns(tableName);
        const columnsToAdd = Object.keys(firstRow).reduce((prev, key) => {

            // Check for a matching record
            const matchingSetting = columnSettings.find(x => x.field === key);

            if (matchingSetting) {
                const columnFormat = matchingSetting?.dateFormat ? matchingSetting.dateFormat : matchingSetting.format;
                const columnDataType = matchingSetting.type;
                const visible = matchingSetting.visible ?? true;

                prev.noChanges.push(<Column key={key} dataField={key} dataType={columnDataType} format={columnFormat}
                    visible={visible}
                    allowReordering={key === "id" || key === "createdOn" ? false : true} 
                    />)
                prev.changesOnly.push(<Column key={key} dataField={key} dataType={columnDataType} format={columnFormat}
                    visible={(key === "id" || key === "createdOn") || changes.includes(key)} 
                    allowReordering={key === "id" || key === "createdOn" ? false : true} 
                    />)
                    return prev;
                };
        
                prev.noChanges.push( <Column key={key} dataField={key}
                    allowReordering={key === "id" || key === "createdOn" ? false : true} 
                    />) 

                prev.changesOnly.push(
                    <Column key={key} dataField={key}
                    visible={(key === "id" || key === "createdOn") || changes.includes(key)} 
                    allowReordering={key === "id" || key === "createdOn" ? false : true} 
                    />
                )
                return prev;
        }, { noChanges: [], changesOnly: [] });
        columnsToAdd.noChanges = columnsToAdd.noChanges.sort((a, b) =>  ["id","createdOn"].includes(b.key) - ["id","createdOn"].includes(a.key))
        columnsToAdd.changesOnly = columnsToAdd.changesOnly.sort((a, b) =>  ["id","createdOn"].includes(b.key) - ["id","createdOn"].includes(a.key))

        return columnsToAdd;
    }

    const createDataTableColumns = (tableName, firstRow) => {

        // Return if there is no data
        if (!firstRow) {
            return;
        }

        const columnSettings = getColumns(tableName);
        const columnsToAdd = Object.keys(firstRow).map(key => {

            // Check for a matching record
            const matchingSetting = columnSettings.find(x => x.field === key);

            if (matchingSetting) {
                const columnFormat = matchingSetting?.dateFormat ? matchingSetting.dateFormat : matchingSetting.format;
                const columnDataType = matchingSetting.type;
                const visible = matchingSetting.visible ?? true;

                return <Column key={key} dataField={key} dataType={columnDataType} format={columnFormat} visible={visible} />;
            }

            return <Column key={key} dataField={key} />;
        });

        return columnsToAdd;
    }

    const copyToClipboard = string => {
        navigator.clipboard.writeText(string);
    }

    const copyCell = cellValue => {
        copyToClipboard(cellValue);
        appNotify(' Cell copied to clipboard.', 'success', { autoHideDuration: 1500 });
    }

    const copyRow = (selectedRows, ref) => {
        const visibleColumns = ref.getVisibleColumns().filter(col => col.dataField);
        const headerString = visibleColumns.map(col => col.caption).join('\t') + '\r\n';
        const combinedString = selectedRows.reduce((string, row) => {
            const rowValues = [];
            visibleColumns.forEach(col => {
                const value = formatColumns(row, col);
                rowValues.push(value == null ? '\xa0' : value);
            });
            return string + rowValues.join('\t') + '\r\n';
        }, headerString);
        copyToClipboard(combinedString);
        appNotify(selectedRows.length + ' row(s) copied to clipboard.', 'success', { autoHideDuration: 1500 });
    }

    const findNestedValue = (row, dataField) => {
        if (!dataField) return undefined;
        let value = row;
        const split = dataField.split('.').filter(el => el !== '');

        for (const el of split) {
            if (value.hasOwnProperty(el)) {
                value = value[el];
            } else {
                value = undefined;
            }
        };
        return value;
    }

    const formatColumns = (row, col) => {
        let value = findNestedValue(row, col.dataField);;
        switch (col.dataType) {
          case 'date':
          case 'datetime':
            return value == null ? '\xa0' : new Date(value).toLocaleString(undefined, {year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hour12: true, minute:'2-digit', second:'2-digit'});
          default:
            return value == null ? '\xa0' : value;
        }
    }

    const handleContextMenuItemClick = e => {
        if (!e.itemData.items) {
            e.itemData.onItemClick();
        }
    }

    const renderContextMenuItem = (contextMenuItem, index) => {
        if (contextMenuItem.text === 'loading') {
            return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}><CircularProgress size={16} /></div>;
        }
        else {
            return <React.Fragment>
                <span style={{ marginRight: '5px' }}>{contextMenuItem.text}</span>
                {contextMenuItem.items ? <span className="dx-icon-spinright" style={{ position: 'absolute', top: '7px', right: '2px' }} /> : null}
            </React.Fragment>;
        }
    }

    const onHiddenContextMenu = e => {
        contextMenuRef.current.instance.option({ items: [], target: '#opportunityHistoryDetailsDialogContent' });
    }

    const handleContextMenu = e => {
        if (e.row?.rowType === 'data') {
            const contextMenuReferences = getContextMenuReferences(e);
            e.items = [];
            showCustomContextMenu(e, contextMenuReferences);
        }
    }

    const getContextMenuReferences = e => {
        if (!e.component.isRowSelected(e.row.key)) {
            e.component.selectRows([e.row.key]);
        }
        return ({
            ref: e.component,
            rows: e.component.getSelectedRowsData()
        });
    }

    const showCustomContextMenu = async (e, contextMenuReferences) => {
        let rows = contextMenuReferences.rows;
        const sortInfo = contextMenuReferences.ref.getDataSource().sort();
        rows = Query(rows).sortBy("id", false).toArray();
        if(sortInfo?.length > 0) {
            rows = sortByThenBy(rows, sortInfo);
        }
        const selectedRows = rows;
        if (!selectedRows.length) return;
        const cellValue = formatColumns(e.row.data, e.column);

        contextMenuRef.current.instance.option({ items: [{ text: "loading" }], target: e.targetElement });
        contextMenuRef.current.instance.show();

        var menuItems = getActionContextMenu(cellValue, selectedRows, contextMenuReferences.ref);
        contextMenuRef.current.instance.option({ items: menuItems, showSubmenuMode: 'onHover' });
    }

    const sortByThenBy = (array, sortInfo) => {
        var query = Query(array).sortBy(sortInfo[0].selector, sortInfo[0].desc);
        for(var i = 1; i < sortInfo.length; i++){
          query = query.thenBy(sortInfo[i].selector, sortInfo[i].desc);
        }
        query = query.thenBy("id", sortInfo[sortInfo.length - 1].desc);
  
        return query.toArray();
      }

    const getActionContextMenu = (cellValue, selectedRows, ref) => {
        const copyCellDisabled = selectedRows.length > 1 ? true : false;
        return [
            {
                text: 'Copy cell',
                disabled: copyCellDisabled,
                onItemClick: () => copyCell(cellValue)
            },
            {
                text: `Copy rows (${selectedRows.length})`,
                disabled: false,
                onItemClick: () => copyRow(selectedRows, ref)
            }
        ];
    }

    const handleChangesOnlyClick = () => {
        hideColumnChooser();
        setViewChangesOnly(!viewChangesOnly);
    }

    const hideColumnChooser = () => {
        secondGridRef.current.instance.hideColumnChooser()
        gridRef.current.instance.hideColumnChooser()
        gridRef.current.instance.refresh()
    }
    const getGridComponent = (dataSource, columns, displayName, option) => {

        
        switch (option) {
            case 'crmHistory':
                const onCellPrepared = (e) => {
                    if (e.rowType === 'data' && e.column.dataField && e.data.changes?.length > 0) {
                        const mapped = e.data.changes?.map(({ fieldName }) => (fieldName.charAt(0).toLowerCase() + fieldName.slice(1)))
                        if(mapped.includes(e.column.dataField)) e.cellElement.classList.add('has-changes');
                    }
                        if (e.rowType === "data" && e.column.command === "expand") {
                        if (e.data.changes?.length === 0) {
                            e.cellElement.classList.remove("dx-datagrid-expand");
                            while (e.cellElement.firstChild) {
                                e.cellElement.removeChild(e.cellElement.firstChild);
                            }
                        }
                    }
                }
                const isViewChangesOnlyDisabled = () => {
                    const rowsWithoutChanges = dataSource?.filter(v => v.changes && v.changes.length === 0);
                    return dataSource.length === rowsWithoutChanges.length;
                };
                
                return (
                    <>
                        <div style={{ display: 'flex' }}>
                            <DialogContentText style={{ paddingTop: '6px' }}> {displayName}</DialogContentText>
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={isViewChangesOnlyDisabled()}
                                style={{ marginLeft: '5px' }}
                                onClick={handleChangesOnlyClick}
                            >
                                {!viewChangesOnly ? 'View Changes Only' : 'Show All Columns'}
                            </Button>
                        </div>
                        <div style={{ display: viewChangesOnly ? 'none' : 'block', marginTop: '60px' }}>
                            <DataGrid
                                dataSource={dataSource} ref={gridRef} onContextMenuPreparing={handleContextMenu}
                                onCellPrepared={onCellPrepared}
                                onRowPrepared={handleHighlightOnRowPrepared}
                                {...gridConfig} keyExpr={gridConfig.gridKeyExpr} style={gridConfig.styleSingle}>
                                <Sorting mode='none' />
                                {columns?.noChanges}
                            </DataGrid>
                        </div>
                        <div style={{ display: viewChangesOnly ? 'block' : 'none' }}>
                            <DataGrid
                                dataSource={dataSource} ref={secondGridRef} onContextMenuPreparing={handleContextMenu}
                                onCellPrepared={onCellPrepared}
                                onRowPrepared={handleHighlightOnRowPrepared}
                                {...gridConfig} keyExpr={gridConfig.gridKeyExpr} style={gridConfig.styleSingle}>
                                <Sorting mode='none' />
                                {columns?.changesOnly}
                                <ColumnChooser
                                    enabled={true}
                                />
                                <Selection mode={'multiple'} showCheckBoxesMode={false} />
                            </DataGrid>
                        </div>
                    </>
                );
                case 'DoubleGrid':
                return (
                    <>
                        <DialogContentText>{displayName.firstGrid}</DialogContentText>
                        <DataGrid dataSource={dataSource.firstGrid} ref={gridRef} onContextMenuPreparing={handleContextMenu}
                            {...gridConfig} style={gridConfig.styleDouble} >
                            <Sorting mode='none' />
                            <Selection mode={'multiple'} showCheckBoxesMode={false} />
                            {columns.firstGrid}
                        </DataGrid>
                        <DialogContentText style={{ marginTop: '1rem' }}>{displayName.secondGrid}</DialogContentText>
                        <DataGrid dataSource={dataSource.secondGrid} ref={secondGridRef} onContextMenuPreparing={handleContextMenu}
                            {...gridConfig} keyExpr={gridConfig.gridKeyExpr} style={gridConfig.styleDouble} onRowPrepared={handleHighlightOnRowPrepared}>
                            <Sorting mode='none' />
                            <Selection mode={'multiple'} showCheckBoxesMode={false} />
                            {columns.secondGrid}
                        </DataGrid>
                    </>
                );
            case 'TripleGrid':
                return (
                    <>
                        <DialogContentText>{displayName.firstGrid}</DialogContentText>
                        <DataGrid dataSource={dataSource.firstGrid} ref={gridRef} onContextMenuPreparing={handleContextMenu}
                            {...gridConfig} style={gridConfig.styleTriple} >
                            <Sorting mode='none' />
                            <Selection mode={'multiple'} showCheckBoxesMode={false} />
                            {columns.firstGrid}
                        </DataGrid>
                        <DialogContentText style={{ marginTop: '1rem' }}>{displayName.secondGrid}</DialogContentText>
                        <DataGrid dataSource={dataSource.secondGrid} ref={secondGridRef} onContextMenuPreparing={handleContextMenu}
                            {...gridConfig} style={gridConfig.styleTriple} >
                            <Sorting mode='none' />
                            <Selection mode={'multiple'} showCheckBoxesMode={false} />
                            {columns.secondGrid}
                        </DataGrid>
                        <DialogContentText style={{ marginTop: '1rem' }}>{displayName.thirdGrid}</DialogContentText>
                        <DataGrid dataSource={dataSource.thirdGrid} ref={thirdGridRef} onContextMenuPreparing={handleContextMenu}
                            {...gridConfig} style={gridConfig.styleTriple} >
                            <Sorting mode='none' />
                            <Selection mode={'multiple'} showCheckBoxesMode={false} />
                            {columns.thirdGrid}
                        </DataGrid>
                    </>
                );
            case 'IcbmGrid':
                return (
                    <>
                        <DialogContentText>{displayName}</DialogContentText>
                        <DataGrid dataSource={dataSource} onRowPrepared={handleIcbmOrdersOnRowPrepared} ref={gridRef} onContextMenuPreparing={handleContextMenu}
                            {...gridConfig} style={gridConfig.styleSingle} >
                            <Sorting mode='none' />
                            <Selection mode={'multiple'} showCheckBoxesMode={false} />
                            {columns}
                        </DataGrid>
                    </>
                );
                case 'Markets':
                    return (
                        <>
                            <DialogContentText>{displayName}</DialogContentText>
                            <DataGrid dataSource={dataSource} onRowPrepared={handleMarketsOnRowPrepared} ref={gridRef} onContextMenuPreparing={handleContextMenu}
                                {...gridConfig} style={gridConfig.styleSingle} >
                                <Sorting mode='none' />
                                <Selection mode={'multiple'} showCheckBoxesMode={false} />
                                {columns}
                            </DataGrid>
                        </>
                    );
            case 'SingleGridWithoutHighlight':
                return (
                    <>
                        <DialogContentText>{displayName}</DialogContentText>
                        <DataGrid dataSource={dataSource} ref={gridRef} onContextMenuPreparing={handleContextMenu}
                            {...gridConfig} style={gridConfig.styleSingle} >
                            <Sorting mode='none' />
                            <Selection mode={'multiple'} showCheckBoxesMode={false} />
                            {columns}
                        </DataGrid>
                    </>
                );
                default :
                return (
                    <>
                        <DialogContentText>{displayName}</DialogContentText>
                        <DataGrid dataSource={dataSource} ref={gridRef} onContextMenuPreparing={handleContextMenu}
                            {...gridConfig} keyExpr={gridConfig.gridKeyExpr} style={gridConfig.styleSingle} onRowPrepared={handleHighlightOnRowPrepared}>
                            <Sorting mode='none' />
                            <Selection mode={'multiple'} showCheckBoxesMode={false} />
                            {columns}
                        </DataGrid>
                    </>
                );
        };
    }


    const opportunityEditHistoriesColumns = useMemo(() => {
        if (opportunityEditHistories) {
            return createDataTableColumns('TransactionOpportunityEditHistories', opportunityEditHistories[0]);
        }
    }, [opportunityEditHistories]);

    const opportunityStatusHistoriesColumns = useMemo(() => {
        if (opportunityStatusHistories) {
            return createDataTableColumns('OpportunityStatusHistories', opportunityStatusHistories[0]);
        }
    }, [opportunityStatusHistories]);

    const opportunityTaxesHistoriesColumns = useMemo(() => {
        if (opportunityTaxesHistories) {
            return createDataTableColumns('Taxes', opportunityTaxesHistories[0]);
        }
    }, [opportunityTaxesHistories]);

    const opportunityCrmReceiptHistoriesColumns = useMemo(() => {
        if (opportunityCrmReceiptHistories) {
            const copy = { ...opportunityCrmReceiptHistories[0] }
            delete copy.changes;
            const columnsWithChanges = []
            opportunityCrmReceiptHistories.forEach(v =>
                v.changes.forEach(({ fieldName }) => {
                    if (!columnsWithChanges.includes(fieldName)) {
                        columnsWithChanges.push(fieldName)
                    }
                }));
            return createDataTableColumnsCrmHist('CrmReceipts', copy, columnsWithChanges)
        }
    }, [opportunityCrmReceiptHistories]);

    const opportunityCrmSubmissionHistoriesColumns = useMemo(() => {
        if (opportunityCrmSubmissionHistories) {
            const copy = { ...opportunityCrmSubmissionHistories[0] }
            delete copy.changes;
            const columnsWithChanges = []
            opportunityCrmSubmissionHistories.forEach(v =>
                v.changes.forEach(({ fieldName }) => {
                    if (!columnsWithChanges.includes(fieldName)) {
                        columnsWithChanges.push(fieldName)
                    }
                }));
            return createDataTableColumnsCrmHist('CrmSubmissions', copy, columnsWithChanges);
        }
    }, [opportunityCrmSubmissionHistories]);

    const opportunityIcbmOrderHistoriesColumns = useMemo(() => {
        if (opportunityIcbmOrderHistories) {
            return createDataTableColumns('IcbmOrders', opportunityIcbmOrderHistories[0]);
        }
    }, [opportunityIcbmOrderHistories]);

    const opportunityImportMarketHistoriesColumns = useMemo(() => {
        if (opportunityImportMarketHistories) {
            return createDataTableColumns('Markets', opportunityImportMarketHistories[0]);
        }
    }, [opportunityImportMarketHistories]);

    const opportunityImportRegionsAndIndicativesHistoriesColumns = useMemo(() => {
        if (opportunityImportRegionsAndIndicativesHistories) {
            return createDataTableColumns('TransactionOpportunityIndicatives', opportunityImportRegionsAndIndicativesHistories[0]);
        }
    }, [opportunityImportRegionsAndIndicativesHistories]);

    const opportunityImportRepairEstimateHistoriesColumns = useMemo(() => {
        if (opportunityImportRepairEstimateHistories) {
            return createDataTableColumns('RepairEstimates', opportunityImportRepairEstimateHistories[0]);
        }
    }, [opportunityImportRepairEstimateHistories]);

    const opportunityImportExpectedSalesPriceHistoriesColumns = useMemo(() => {
        if (opportunityImportExpectedSalesPriceHistories) {
            return createDataTableColumns('ExpectedSalesPrice', opportunityImportExpectedSalesPriceHistories[0]);
        }
    }, [opportunityImportExpectedSalesPriceHistories]);

    const opportunityImportInsuranceHistoriesColummns = useMemo(() => {
        if (opportunityImportInsuranceHistories) {
            return createDataTableColumns('Insurances', opportunityImportInsuranceHistories[0]);
        }
    }, [opportunityImportInsuranceHistories]);

    const opportunityImportCompHistoriesColumns = useMemo(() => {
        if (opportunityImportCompHistories) {
            return createDataTableColumns('Comps', opportunityImportCompHistories[0]);
        }
    }, [opportunityImportCompHistories]);

    const opportunityValuationHistoriesColumns = useMemo(() => {
        if (opportunityValuationHistories) {
            return createDataTableColumns('ValuationHistories', opportunityValuationHistories[0]);
        }
    }, [opportunityValuationHistories]);

    const opportunityAllocationHistoriesColumns = useMemo(() => {
        if (opportunityAllocationHistories) {
            return createDataTableColumns('Allocations', opportunityAllocationHistories[0]);
        }
    }, [opportunityAllocationHistories]);

    const opportunityImportHomesHistoriesColumns = useMemo(() => {
        if (opportunityImportHomesHistories) {
            return createDataTableColumns('Homes', opportunityImportHomesHistories[0]);
        }
    }, [opportunityImportHomesHistories]);

    const opportunityTransactionOpportunityHistoriesColumns = useMemo(() => {
        if (opportunityTransactionOpportunityHistories) {
            return createDataTableColumns('TransactionOpportunityHistories', opportunityTransactionOpportunityHistories[0]);
        }
    }, [opportunityTransactionOpportunityHistories]);

    const opportuniityProformaInputsContainersColumns = useMemo(() => {
        if (opportunityProformaInputsContainers) {
            return createDataTableColumns('ProformaInputsContainers', opportunityProformaInputsContainers[0]);
        }
    }, [opportunityProformaInputsContainers]);

    const opportunityColumns = useMemo(() => {
        if (opportunity) {
            return createDataTableColumns('TransactionOpportunities', opportunity[0]);
        }
    }, [opportunity]);

    const prospectsColumns = useMemo(() => {
        if (opportunityProspects) {
            return createDataTableColumns('Prospects', opportunityProspects[0]);
        }
    }, [opportunityProspects]);

    const importsColumns = useMemo(() => {
        if (opportunityImportSaveHistories && opportunityImportSaveHistories.imports) {
            return createDataTableColumns('Imports', opportunityImportSaveHistories.imports[0]);
        }
    }, [opportunityImportSaveHistories]);

    const opportunityImportSaveHistoriesImportPropertiesColumns = useMemo(() => {
        if (opportunityImportSaveHistories && opportunityImportSaveHistories.importProperties) {
            return createDataTableColumns('ImportProperties', opportunityImportSaveHistories.importProperties[0]);
        }
    }, [opportunityImportSaveHistories]);

    const opportunityImportPropMasterHistoriesImportPropertiesColumns = useMemo(() => {
        if (opportunityImportPropMasterHistories && opportunityImportPropMasterHistories.importProperties) {
            return createDataTableColumns('ImportProperties', opportunityImportPropMasterHistories.importProperties[0]);
        }
    }, [opportunityImportPropMasterHistories]);

    const opportunityImportPropMasterHistoriesPMResultsColumns = useMemo(() => {
        if (opportunityImportPropMasterHistories && opportunityImportPropMasterHistories.pmResults) {
            return createDataTableColumns('PMResults', opportunityImportPropMasterHistories.pmResults[0]);
        }
    }, [opportunityImportPropMasterHistories]);

    const opportunityImportPropMasterHistoriesPMNearMatchesColumns = useMemo(() => {
        if (opportunityImportPropMasterHistories && opportunityImportPropMasterHistories.pmNearMatches) {
            return createDataTableColumns('PMNearMatches', opportunityImportPropMasterHistories.pmNearMatches[0]);
        }
    }, [opportunityImportPropMasterHistories]);

    const rejectedImportsColumns = useMemo(() => {
        if (opportunityRejectedImports && opportunityRejectedImports.rejectedImports) {
            return createDataTableColumns('RejectedImports', opportunityRejectedImports.rejectedImports[0]);
        }
    }, [opportunityRejectedImports]);

    const rejectedImportItemsColumns = useMemo(() => {
        if (opportunityRejectedImports && opportunityRejectedImports.rejectedImportItems) {
            return createDataTableColumns('RejectedImportItems', opportunityRejectedImports.rejectedImportItems[0]);
        }
    }, [opportunityRejectedImports]);

    const opportunityAssignmentHistoriesColumns = useMemo(() => {
        if (opportunityAssignmentHistories) {
            return createDataTableColumns('OpportunityAssignmentHistories', opportunityAssignmentHistories[0]);
        }
    }, [opportunityAssignmentHistories]);

    const opportunityUnderwriteStatusHistoriesColumns = useMemo(() => {
        if (opportunityUnderwriteStatusHistories) {
            return createDataTableColumns('OpportunityUnderwriteStatusHistories', opportunityUnderwriteStatusHistories[0]);
        }
    }, [opportunityUnderwriteStatusHistories]);

    const formatDate = (dateToFormat) => {
        return format(parseISO(dateToFormat), 'MM/dd/yyyy, h:mm:ss a');
    }

    return (
        <>
            {eventSummary &&
                <Dialog onClose={handleDetailsClose} open={isViewOpportunityHistoryDetailsModalOpen} className={classes.dialog} maxWidth={'xl'} fullWidth={true}>
                    <DialogTitle>
                        {`${eventSummary?.eventType} - ${txnOppId ? txnOppId : ''} ${eventSummary?.eventDate ? (formatDate(eventSummary?.eventDate)) : ''}`}
                    </DialogTitle>
                    <IconButton className={classes.closeButton} onClick={handleDetailsClose}>
                        <ClearIcon fontSize='inherit' />
                    </IconButton>
                    <DialogContent id='opportunityHistoryDetailsDialogContent' >
                        {isViewOpportunityHistoryDetailsModalLoading &&
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <CircularProgress />
                            </div>
                        }

                        {!isViewOpportunityHistoryDetailsModalLoading && isViewOpportunityHistoryDetailsModalOpen &&
                            <>
                                {opportunityEditHistories &&
                                    getGridComponent(opportunityEditHistories, opportunityEditHistoriesColumns, 'TransactionOpportunityEditHistories')
                                }

                                {opportunityStatusHistories &&
                                    getGridComponent(opportunityStatusHistories, opportunityStatusHistoriesColumns, 'OpportunityStatusHistories')
                                }

                                {opportunityTaxesHistories &&
                                    getGridComponent(opportunityTaxesHistories, opportunityTaxesHistoriesColumns, 'Taxes')
                                }

                                {opportunityCrmSubmissionHistories &&
                                    getGridComponent(opportunityCrmSubmissionHistories, opportunityCrmSubmissionHistoriesColumns, 'CrmSubmissions', 'crmHistory')
                                }

                                {opportunityCrmReceiptHistories &&
                                    getGridComponent(opportunityCrmReceiptHistories, opportunityCrmReceiptHistoriesColumns, 'CrmReceipts', 'crmHistory')
                                }

                                {opportunityAllocationHistories &&
                                    getGridComponent(opportunityAllocationHistories, opportunityAllocationHistoriesColumns, 'Allocations')
                                }

                                {opportunityImportSaveHistories &&
                                    getGridComponent({
                                        firstGrid: opportunityImportSaveHistories.imports,
                                        secondGrid: opportunityImportSaveHistories.importProperties
                                    },
                                        {
                                            firstGrid: importsColumns,
                                            secondGrid: opportunityImportSaveHistoriesImportPropertiesColumns
                                        },
                                        { firstGrid: 'Imports', secondGrid: 'ImportProperties' }, 'DoubleGrid')
                                }

                                {opportunityImportPropMasterHistories &&
                                    getGridComponent({
                                        firstGrid: opportunityImportPropMasterHistories.importProperties,
                                        secondGrid: opportunityImportPropMasterHistories.pmResults,
                                        thirdGrid: opportunityImportPropMasterHistories.pmNearMatches
                                    },
                                        {
                                            firstGrid: opportunityImportPropMasterHistoriesImportPropertiesColumns,
                                            secondGrid: opportunityImportPropMasterHistoriesPMResultsColumns,
                                            thirdGrid: opportunityImportPropMasterHistoriesPMNearMatchesColumns
                                        },
                                        { firstGrid: 'ImportProperties', secondGrid: 'PMResults', thirdGrid: 'PMNearMatches' }, 'TripleGrid')
                                }

                                {opportunityImportHomesHistories &&
                                    getGridComponent(opportunityImportHomesHistories, opportunityImportHomesHistoriesColumns, 'Homes', 'SingleGridWithoutHighlight')
                                }

                                {opportunityImportRegionsAndIndicativesHistories &&
                                    getGridComponent(opportunityImportRegionsAndIndicativesHistories, opportunityImportRegionsAndIndicativesHistoriesColumns, 'GUD Indicatives')
                                }

                                {opportunityImportMarketHistories &&
                                    getGridComponent(opportunityImportMarketHistories, opportunityImportMarketHistoriesColumns, 'Markets', 'Markets')
                                }

                                {opportunityIcbmOrderHistories &&
                                    getGridComponent(opportunityIcbmOrderHistories, opportunityIcbmOrderHistoriesColumns, 'IcbmOrders', 'IcbmGrid')
                                }

                                {opportunityImportRepairEstimateHistories &&
                                    getGridComponent(opportunityImportRepairEstimateHistories, opportunityImportRepairEstimateHistoriesColumns, 'RepairEstimates')
                                }

                                {opportunityImportExpectedSalesPriceHistories &&
                                    getGridComponent(opportunityImportExpectedSalesPriceHistories, opportunityImportExpectedSalesPriceHistoriesColumns, 'ExpectedSalesPrice')
                                }

                                {opportunityImportInsuranceHistories &&
                                    getGridComponent(opportunityImportInsuranceHistories, opportunityImportInsuranceHistoriesColummns, 'Insurances')
                                }

                                {opportunityImportCompHistories &&
                                    getGridComponent(opportunityImportCompHistories, opportunityImportCompHistoriesColumns, 'Comps', 'SingleGridWithoutHighlight')
                                }

                                {opportunityValuationHistories &&
                                    getGridComponent(opportunityValuationHistories, opportunityValuationHistoriesColumns, 'ValuationHistories')
                                }

                                {opportunityTransactionOpportunityHistories &&
                                    getGridComponent(opportunityTransactionOpportunityHistories, opportunityTransactionOpportunityHistoriesColumns, 'TransactionOpportunityHistories')
                                }

                                {opportunityAllocationLogs &&
                                    <>
                                        <DialogContentText>Allocation Logs</DialogContentText>
                                        <DialogContentText>
                                            <Typography style={{ whiteSpace: 'pre-wrap' }}>{opportunityAllocationLogs}</Typography>
                                        </DialogContentText>
                                    </>
                                }

                                {opportunityImportLogs &&
                                    <>
                                        <DialogContentText>{eventSummary !== null && `${eventSummary.eventType} Logs`}</DialogContentText>
                                        <DialogContentText>
                                            <Typography style={{ whiteSpace: 'pre-wrap' }}>{opportunityImportLogs}</Typography>
                                        </DialogContentText>
                                    </>
                                }

                                {opportunityProformaInputsContainers &&
                                    getGridComponent(opportunityProformaInputsContainers, opportuniityProformaInputsContainersColumns, 'ProformaInputsContainers')
                                }

                                {opportunity &&
                                    getGridComponent(opportunity, opportunityColumns, 'TransactionOpportunities')
                                }

                                {opportunityProspects &&
                                    getGridComponent(opportunityProspects, prospectsColumns, 'Prospects')
                                }

                                {opportunityAssignmentHistories &&
                                    getGridComponent(opportunityAssignmentHistories, opportunityAssignmentHistoriesColumns, 'OpportunityAssignmentHistories')
                                }

                                {opportunityUnderwriteStatusHistories &&
                                    getGridComponent(opportunityUnderwriteStatusHistories, opportunityUnderwriteStatusHistoriesColumns, 'OpportunityUnderwriteStatusHistories')
                                }

                                {opportunityRejectedImports &&
                                    getGridComponent({
                                        firstGrid: opportunityRejectedImports.rejectedImports,
                                        secondGrid: opportunityRejectedImports.rejectedImportItems
                                    },
                                        {
                                            firstGrid: rejectedImportsColumns,
                                            secondGrid: rejectedImportItemsColumns
                                        },
                                        { firstGrid: 'RejectedImports', secondGrid: 'RejectedImportItems' }, 'DoubleGrid')
                                }
                                <ContextMenu
                                    ref={contextMenuRef}
                                    onItemClick={handleContextMenuItemClick}
                                    itemRender={renderContextMenuItem}
                                    onHidden={onHiddenContextMenu}
                                    target='#opportunityHistoryDetailsDialogContent'
                                />
                            </>
                        }
                    </DialogContent>
                    <DialogActions>
                        <></>
                    </DialogActions>
                </Dialog>}
        </>
    )
}

const styles = theme => ({
    dialog: {
        minWidth: '700px',
        minHeight: '150px'
    },
    textFieldRoot: {
        height: 70,
        marginBottom: 16,
        paddingRight: 8,
        flexBasis: '25%'
    },
    errorText: {
        lineHeight: 1
    },
    textWrapper: {
        display: 'flex',
        justifyContent: 'space-between'
    },
    textLabel: {
        minWidth: '200px'
    },
    divider: {
        marginTop: '4px',
        marginBottom: '4px'
    },
    closeButton: {
        position: 'absolute',
        right: 0,
        top: 0,
        backgroundColor: '#ffffff'
    }
});

const mapStateToProps = (state) => {

    const { opportunityHistoryState } = state;



    return {
        eventSummary: opportunityHistoryState.eventSummaryRowData,
        isViewOpportunityHistoryDetailsModalLoading: opportunityHistoryState.isViewOpportunityHistoryDetailsModalLoading,
        isViewOpportunityHistoryDetailsModalOpen: opportunityHistoryState.isViewOpportunityHistoryDetailsModalOpen,
        opportunityEditHistories: opportunityHistoryState.opportunityEditHistories,
        opportunityStatusHistories: opportunityHistoryState.opportunityStatusHistories,
        opportunityTaxesHistories: opportunityHistoryState.opportunityTaxesHistories,
        opportunityCrmSubmissionHistories: opportunityHistoryState.opportunityCrmSubmissionHistories,
        opportunityAllocationHistories: opportunityHistoryState.opportunityAllocationHistories,
        opportunityCrmReceiptHistories: opportunityHistoryState.opportunityCrmReceiptHistories,
        opportunityValuationHistories: opportunityHistoryState.opportunityValuationHistories,
        opportunityIcbmOrderHistories: opportunityHistoryState.opportunityIcbmOrderHistories,
        opportunityImportCompHistories: opportunityHistoryState.opportunityImportCompHistories,
        opportunityImportSaveHistories: opportunityHistoryState.opportunityImportSaveHistories,
        opportunityImportPropMasterHistories: opportunityHistoryState.opportunityImportPropMasterHistories,
        opportunityImportHomesHistories: opportunityHistoryState.opportunityImportHomesHistories,
        opportunityImportRegionsAndIndicativesHistories: opportunityHistoryState.opportunityImportRegionsAndIndicativesHistories,
        opportunityImportMarketHistories: opportunityHistoryState.opportunityImportMarketHistories,
        opportunityImportRepairEstimateHistories: opportunityHistoryState.opportunityImportRepairEstimateHistories,
        opportunityImportExpectedSalesPriceHistories: opportunityHistoryState.opportunityImportExpectedSalesPriceHistories,
        opportunityImportInsuranceHistories: opportunityHistoryState.opportunityImportInsuranceHistories,
        opportunityTransactionOpportunityHistories: opportunityHistoryState.opportunityTransactionOpportunityHistories,
        opportunityProformaInputsContainers: opportunityHistoryState.opportunityProformaInputsContainers,
        opportunity: opportunityHistoryState.opportunity,
        opportunityProspects: opportunityHistoryState.opportunityProspects,
        opportunityEventHistories: opportunityHistoryState.opportunityEventHistories,
        opportunityRejectedImports: opportunityHistoryState.opportunityRejectedImports,
        opportunityAllocationLogs: opportunityHistoryState.opportunityAllocationLogs,
        opportunityImportLogs: opportunityHistoryState.opportunityImportLogs,
        opportunityAssignmentHistories: opportunityHistoryState.opportunityAssignmentHistories,
        opportunityUnderwriteStatusHistories: opportunityHistoryState.opportunityUnderwriteStatusHistories,
        appSettings: state.appState.appSettings
    };
};

const mapDispatchToProps = {
    fetchOpportunityHistoryTableDetails,
    closeViewOpportunityHistoryDetails,
    appNotify
};

export default compose(
    withStyles(styles),
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(ViewOpportunityHistoryDetailsModal);
